import { useCallback, useState } from 'react';
import {
  Box, Stack, Typography, Button, Alert, Grid, Card, CardActionArea, Radio,
  Divider, Dialog, DialogTitle, DialogContent, IconButton, TextField, InputAdornment,
  CircularProgress, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Chip,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch, useSelector } from 'react-redux';
import { setAgreement, setGstStance, setChargeMode, setCustomSlabParents } from '../../../../redux/pricingWizardSlice';
import { uniqueSelectedParents } from '../helpers/workbookTemplateV2';
import { useUploadAgreementMutation } from '../../../../redux/wizardApiSlice';
import SectionTitle from '../components/SectionTitle';
import CreditCard from '../components/CreditCard';
import RateCardSetupCard from '../components/RateCardSetupCard';
import ArrangementBlock from '../components/ArrangementBlock';
import { useSnackbar } from 'notistack';

export default function Stage2_Agreement() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const agreement = useSelector((s) => s.pricingWizard.agreement);
  const merchantId = useSelector((s) => s.pricingWizard.merchantId);
  const codChargeMode = useSelector((s) => s.pricingWizard.codChargeMode);
  const rtoChargeMode = useSelector((s) => s.pricingWizard.rtoChargeMode);
  const customSlabParents = useSelector((s) => s.pricingWizard.customSlabParents);
  const wizardState = useSelector((s) => s.pricingWizard);
  const parentOptions = uniqueSelectedParents(wizardState);
  const [dragOver, setDragOver] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  const [uploadAgreement, { isLoading: uploading }] = useUploadAgreementMutation();

  const ingestFile = useCallback(async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      enqueueSnackbar('Please pick a .pdf file', { variant: 'warning' });
      return;
    }
    // Preview locally (base64 data URL for in-browser PDF viewer)
    const reader = new FileReader();
    reader.onload = (ev) => dispatch(setAgreement({ pdfDataUrl: ev.target.result, filename: file.name }));
    reader.readAsDataURL(file);
    // Upload to server for compliance storage — s3Key stored in Redux via onQueryStarted
    if (merchantId) {
      try {
        await uploadAgreement({ merchantId, file }).unwrap();
        enqueueSnackbar('Agreement uploaded to server', { variant: 'success', autoHideDuration: 2000 });
      } catch {
        enqueueSnackbar('Failed to upload agreement to server — PDF is still attached locally.', { variant: 'warning', autoHideDuration: 3000 });
      }
    }
  }, [dispatch, enqueueSnackbar, merchantId, uploadAgreement]);

  const handleFileChange = (e) => ingestFile(e.target.files?.[0]);
  const replacePdf = () => dispatch(setAgreement({ pdfDataUrl: null, filename: '', s3Key: null, s3Url: null }));
  // hasPdf: local base64, uploaded s3Key, or server URL from API
  const hasPdf = Boolean(agreement.pdfDataUrl || agreement.s3Key || agreement.s3Url);
  // Prefer local base64 for preview; fall back to server URL
  const previewSrc = agreement.pdfDataUrl || agreement.s3Url;

  return (
    <Box>
      {/* Credit Limit — recommended limit, Sales Leader override and the approval
          upload. The Payment & Credit inputs it derives from now live on Stage 1. */}
      <CreditCard section="limit" />

      {/* Rate Card Setup (Blended vs Courier-Wise) */}
      <RateCardSetupCard />

      <Divider sx={{ my: 2 }} />

      {/* Signed agreement — stored attachment (no extraction) */}
      <SectionTitle icon="📎" title="Signed Commercial Agreement" badge="required attachment" />
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, mb: 1.5 }}>
        Attach the signed commercial agreement (PDF) — required before continuing. The commercial construct below is entered manually.
      </Typography>

      {hasPdf ? (
        <Alert
          severity="success"
          icon={<DescriptionOutlinedIcon fontSize="small" />}
          sx={{ borderRadius: 2 }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              {previewSrc && (
                <Button size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                  onClick={() => setPdfPreviewOpen(true)}>Preview PDF</Button>
              )}
              <Button size="small" color="inherit" startIcon={<RefreshIcon sx={{ fontSize: 13 }} />}
                onClick={replacePdf}>Replace</Button>
            </Stack>
          }
        >
          {agreement.filename || 'Agreement PDF'} attached
          {uploading && <CircularProgress size={12} sx={{ ml: 1 }} />}
          {(agreement.s3Key || agreement.s3Url) && !uploading && ' · saved to server'}
        </Alert>
      ) : (
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); ingestFile(e.dataTransfer.files?.[0]); }}
          sx={{
            p: 4, textAlign: 'center', border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            bgcolor: dragOver ? 'primary.lighter' : 'background.neutral',
            borderRadius: 1.5, transition: 'all .15s',
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 38, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ fontWeight: 700, mt: 1 }}>
            Drag the signed PDF here, or{' '}
            <Box component="label" htmlFor="pdf-upload-input" sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}>
              browse to choose a file
            </Box>
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Accepts a single .pdf file</Typography>
          <input id="pdf-upload-input" type="file" accept="application/pdf" hidden onChange={handleFileChange} />
        </Box>
      )}

      <Dialog open={pdfPreviewOpen} onClose={() => setPdfPreviewOpen(false)}
        maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2.5, height: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', py: 1.5, pr: 1.5, gap: 1 }}>
          <DescriptionOutlinedIcon sx={{ color: 'text.secondary' }} />
          <Typography sx={{ fontWeight: 700, flex: 1, fontSize: 15 }} noWrap>{agreement.filename || 'Agreement PDF'}</Typography>
          <IconButton size="small" onClick={() => setPdfPreviewOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
          <Box component="embed" src={`${previewSrc}#toolbar=1&navpanes=0`} type="application/pdf"
            sx={{ width: '100%', height: '100%', border: 0, display: 'block' }} />
        </DialogContent>
      </Dialog>

      {/* Manual commercial construct */}
      <Box sx={{ mt: 2.5 }}>
        <SectionTitle icon="🧾" title="Commercial Construct" badge="manual" />

        {/* GST stance */}
        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, display: 'block', mt: 1 }}>
          GST stance
        </Typography>
        <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
          {[
            { id: 'exclusive', title: 'Exclusive of GST', sub: 'Rates in the agreement are ex-GST · no conversion needed.' },
            { id: 'inclusive', title: 'Inclusive of GST', sub: 'System divides base rates & T&Cs by 1.18 to derive ex-GST values.' },
          ].map((opt) => {
            const checked = agreement.gst_stance === opt.id;
            return (
              <Grid item xs={12} md={6} key={opt.id}>
                <Card variant="outlined" sx={{ borderColor: checked ? 'primary.main' : 'divider', bgcolor: checked ? 'primary.lighter' : 'background.paper' }}>
                  <CardActionArea onClick={() => dispatch(setGstStance(opt.id))} sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Radio checked={checked} sx={{ p: 0.25, pointerEvents: 'none' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{opt.title}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>{opt.sub}</Typography>
                      </Box>
                    </Stack>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* VC + weight ceiling */}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField size="small" fullWidth type="number" label="Volumetric Coefficient"
              value={agreement.vc || ''}
              onChange={(e) => dispatch(setAgreement({ vc: Number(e.target.value) || 0 }))}
              helperText="Divisor used to convert volume (cm³) to volumetric weight." />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField size="small" fullWidth type="number" label="Weight Ceiling (grams)"
              value={agreement.weight_ceiling || ''}
              onChange={(e) => dispatch(setAgreement({ weight_ceiling: Number(e.target.value) || 0 }))}
              InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }}
              helperText="Max chargeable weight per shipment." />
          </Grid>
        </Grid>

        {/* COD / RTO charge modes — drive which sheets the rate-card workbook carries
            (Rate Card Modification MoM): FIXED → T&C values apply; VARIABLE → the COD /
            RTO sheet is included in the download and must be filled before upload. */}
        {[
          {
            kind: 'cod', value: codChargeMode, title: 'COD charge',
            fixedSub: 'One % / fixed amount (with Min/Max operator) — from T&Cs.',
            varSub: 'Order-value bands — the workbook gets a COD sheet to fill.',
          },
          {
            kind: 'rto', value: rtoChargeMode, title: 'RTO charge',
            fixedSub: 'Single % of forward freight — from T&Cs.',
            varSub: '% of forward per zone × slab — the workbook gets an RTO sheet. (% only; flat ₹ not supported.)',
          },
        ].map((cfg) => (
          <Box key={cfg.kind} sx={{ mt: 2 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
              {cfg.title} · Fixed or Variable
            </Typography>
            <Grid container spacing={1.5} sx={{ mt: 0.1 }}>
              {[
                { id: 'FIXED', title: `${cfg.title} — Fixed`, sub: cfg.fixedSub },
                { id: 'VARIABLE', title: `${cfg.title} — Variable`, sub: cfg.varSub },
              ].map((opt) => {
                const checked = cfg.value === opt.id;
                return (
                  <Grid item xs={12} md={6} key={opt.id}>
                    <Card variant="outlined" sx={{ borderColor: checked ? 'primary.main' : 'divider', bgcolor: checked ? 'primary.lighter' : 'background.paper' }}>
                      <CardActionArea onClick={() => dispatch(setChargeMode({ kind: cfg.kind, mode: opt.id }))} sx={{ p: 1.25 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Radio checked={checked} sx={{ p: 0.25, pointerEvents: 'none' }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{opt.title}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{opt.sub}</Typography>
                          </Box>
                        </Stack>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}

        {/* Custom weight slabs — parents whose ladder differs from the template
            (e.g. DTDC). They move to "(Custom)" workbook sheets with an editable
            Min/Max ladder; ONE shared ladder for all of them. */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
            Custom weight slabs
          </Typography>
          <Grid container spacing={1.5} sx={{ mt: 0.1 }}>
            {[
              { id: false, title: 'No — default slabs', sub: 'Every courier uses the slab template ladder.' },
              { id: true, title: 'Yes — some couriers have custom slabs', sub: 'Selected couriers move to "(Custom)" sheets with an editable slab ladder (one shared ladder).' },
            ].map((opt) => {
              const checked = !!customSlabParents.enabled === opt.id;
              return (
                <Grid item xs={12} md={6} key={String(opt.id)}>
                  <Card variant="outlined" sx={{ borderColor: checked ? 'primary.main' : 'divider', bgcolor: checked ? 'primary.lighter' : 'background.paper' }}>
                    <CardActionArea onClick={() => dispatch(setCustomSlabParents({ enabled: opt.id }))} sx={{ p: 1.25 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Radio checked={checked} sx={{ p: 0.25, pointerEvents: 'none' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{opt.title}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{opt.sub}</Typography>
                        </Box>
                      </Stack>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {!!customSlabParents.enabled && (
            <>
              {/* Fixed-size field + anchored, height-capped menu: the field never grows
                  with the selection, so the open menu stays put while ticking. */}
              <FormControl size="small" sx={{ mt: 1.5, width: 340 }}>
                <InputLabel id="custom-slab-parents-label">Couriers with custom slabs</InputLabel>
                <Select
                  labelId="custom-slab-parents-label"
                  label="Couriers with custom slabs"
                  multiple
                  value={customSlabParents.parents || []}
                  onChange={(e) => dispatch(setCustomSlabParents({ parents: e.target.value }))}
                  renderValue={(sel) => `${sel.length} courier${sel.length === 1 ? '' : 's'} selected`}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 320, width: 320 } },
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                  }}
                >
                  {parentOptions.map((p) => (
                    <MenuItem key={p} value={p} dense>
                      <Checkbox size="small" checked={(customSlabParents.parents || []).includes(p)} />
                      <ListItemText primary={p} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Selections as removable chips; deleting one also unticks it in the menu
                  (the checkboxes render from the same value). */}
              {(customSlabParents.parents || []).length > 0 && (
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  {(customSlabParents.parents || []).map((p) => (
                    <Chip
                      key={p}
                      label={p}
                      size="small"
                      onDelete={() => dispatch(setCustomSlabParents({
                        parents: (customSlabParents.parents || []).filter((x) => x !== p),
                      }))}
                    />
                  ))}
                </Stack>
              )}
            </>
          )}
          {!!customSlabParents.enabled && !parentOptions.length && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.main' }}>
              No couriers selected on the Profile step yet — pick services/couriers there first.
            </Typography>
          )}
        </Box>
      </Box>

      {/* Per-service arrangement + RVP config */}
      <ArrangementBlock />
    </Box>
  );
}

export function validateAgreement(agreement, rvp, customSlabParents) {
  // Fully manual flow: require a GST stance and the signed agreement PDF.
  const errors = {};
  if (!agreement.pdfDataUrl && !agreement.s3Key && !agreement.s3Url) errors.pdf = 'Attach the signed commercial agreement PDF before continuing.';
  if (!agreement.gst_stance) errors.gst_stance = 'Select the GST stance for the commercial construct.';
  // Custom slabs enabled with nothing selected = nothing would move to the
  // (Custom) sheets — force an explicit courier choice or switch back to No.
  if (customSlabParents && customSlabParents.enabled && !(customSlabParents.parents || []).length) {
    errors.custom_slabs = 'Custom slabs is set to Yes — select at least one courier, or switch it back to No.';
  }
  // Mode B (independent RVP): the QC charge is mandatory — with-QC prices are derived
  // from the uploaded RVP sheet as (sheet value + QC charge). 0 is a valid entry.
  if (rvp && rvp.enabled && rvp.mode === 'independent') {
    const qc = rvp.tcs && rvp.tcs.qc_amount;
    if (qc === '' || qc == null || isNaN(Number(qc))) {
      errors.rvp_qc = 'Enter the RVP QC charge (₹) in the RVP configuration — required for Independent (Mode B) RVP.';
    }
  }
  return errors;
}
